/**
 * URL helpers for banquet event order PDFs.
 * WebViewer fetches `initialDoc` via XHR in a worker, so unencoded paths and
 * API IRIs passed as file URLs show up as a generic "NetworkError".
 */

export function encodeRemoteFileUrl(url: string): string {
	if (!url) return '';

	const encodePath = (path: string): string =>
		path
			.split('/')
			.map((segment) => {
				if (!segment) return segment;
				try {
					return encodeURIComponent(decodeURIComponent(segment));
				} catch {
					return encodeURIComponent(segment);
				}
			})
			.join('/');

	const match = url.match(/^(https?:\/\/[^/]+)(\/.*)$/i);
	if (!match) return url;

	const [, origin, rest] = match;
	const queryIndex = rest.indexOf('?');
	const path = queryIndex === -1 ? rest : rest.slice(0, queryIndex);
	const query = queryIndex === -1 ? '' : rest.slice(queryIndex);
	return `${origin}${encodePath(path)}${query}`;
}

/** True when `ref` is an API resource IRI (relative or absolute), not a file URL. */
export function isApiResourceReference(ref: string): boolean {
	const value = String(ref || '').trim();
	if (!value) return false;
	if (/^https?:\/\/[^/]+\/api\//i.test(value)) return true;
	if (value.startsWith('/api/')) return true;
	if (value.startsWith('api/')) return true;
	return false;
}

/** Public uploads URL for a filled/uploaded event document (not a template). */
export function buildUploadedEventDocumentUrl(filePath: string): string {
	const uploadsBase = (process.env.REACT_APP_IMAGE_URL || '').replace(/\/$/, '');
	if (!filePath || !uploadsBase) return '';

	if (/^https?:\/\//i.test(filePath)) {
		return encodeRemoteFileUrl(filePath);
	}

	let clean = String(filePath).replace(/^\/+/, '').replace(/^uploads\//, '');
	if (!clean.startsWith('event_documents/')) {
		clean = `event_documents/${clean}`;
	}

	return encodeRemoteFileUrl(`${uploadsBase}/${clean}`);
}
